import { Stack } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs from 'dayjs';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ClockAddIcon } from '@/shared/assets/icons/clock-add-icon';
import { Alert } from '@/shared/components/ui/alert/alert';
import { IconButton } from '@/shared/components/ui/button/icon-button/icon-button';
import { FormDateTimePickerContainer } from '@/shared/components/ui/form/components/form-date-time-picker/form-date-time-picker.styled';
import { TFormDatePickerProps } from '@/shared/components/ui/form/components/form-date-time-picker/form-date-time-picker.types';
import { FormLabel } from '@/shared/components/ui/form/components/form-label/form-label';
import { useValidationMessage } from '@/shared/components/ui/form/hooks/use-validation-message/use-validation-message';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';

export const FormDatePicker = <TFieldValues extends FieldValues>({
  name,
  label,
  defaultValue,
  rules,
  shouldUnregister,
  hint,
  triggerIcon,
  triggerLabel,
  dateTimePickerProps,
  futureOnly = false,
  pastOnly = false,
  triggerElement,
  required,
  disabled,
  dateOnly = false,
  ...rest
}: TFormDatePickerProps<TFieldValues>) => {
  const { t, i18n } = useTranslation();
  const { control, trigger } = useFormContext<TFieldValues>();
  const resolveValidationMessage = useValidationMessage();
  const locale = i18n.language.split('-')[0];

  const getDatePickerProps = () => {
    const props = { ...dateTimePickerProps };
    if (futureOnly) props.minDate = dayjs().startOf('day');
    if (pastOnly) props.maxDate = dayjs().endOf('day');
    return props;
  };

  // Store the selected calendar day as UTC start-of-day so consumers can derive
  // the exact boundary (e.g. end-of-day) without timezone drift.
  const handleDateChange = (
    value: dayjs.Dayjs | null,
    onChange: (value: string | null) => void,
  ) => {
    if (value) {
      const dateValue = dayjs
        .utc()
        .year(value.year())
        .month(value.month())
        .date(value.date())
        .startOf('day');
      onChange(
        dateOnly ? dateValue.format('YYYY-MM-DD') : dateValue.toISOString(),
      );
    } else {
      onChange(null);
    }
    void trigger(name);
  };

  const renderTrigger = (value: string | null) => {
    if (triggerElement) return triggerElement(value);

    const className = value ? 'active-icon-button' : undefined;

    if (triggerLabel) {
      return (
        <IconButton className={className} disabled={disabled}>
          {triggerIcon || <ClockAddIcon />}
          {triggerLabel(value)}
        </IconButton>
      );
    }

    return (
      <IconButton
        aria-label={label || t('ui:select_date')}
        className={className}
        tooltip={label}
        disabled={disabled}
      >
        {triggerIcon || <ClockAddIcon />}
      </IconButton>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      disabled={disabled}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <FormDateTimePickerContainer gap="0.75rem" width="100%">
          <Stack
            direction={triggerElement ? 'column' : 'row'}
            gap="0.5rem"
            alignItems="center"
          >
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={locale}
            >
              <Stack width={triggerElement ? '100%' : 'auto'}>
                {triggerElement && label && (
                  <FormLabel label={label} required={required} />
                )}
                <PopoverController trigger={renderTrigger(value)} {...rest}>
                  {(handleClose) => (
                    <StaticDatePicker
                      {...getDatePickerProps()}
                      disabled={disabled}
                      value={value ? dayjs.utc(value) : null}
                      onChange={(newValue) =>
                        handleDateChange(
                          newValue ? dayjs(newValue) : null,
                          onChange,
                        )
                      }
                      views={['year', 'month', 'day']}
                      openTo="day"
                      localeText={{ toolbarTitle: t('ui:select_date') }}
                      slotProps={{
                        actionBar: { actions: ['cancel', 'accept'] },
                      }}
                      onAccept={handleClose}
                      onClose={handleClose}
                    />
                  )}
                </PopoverController>
              </Stack>
            </LocalizationProvider>
            {error && (
              <Alert
                color="red"
                text={
                  resolveValidationMessage(error.message) ||
                  t('ui:invalid_date')
                }
              />
            )}
          </Stack>
          {hint && <Alert color="blue" text={hint} />}
        </FormDateTimePickerContainer>
      )}
    />
  );
};
